import React from "react";
import Chip from "@material-ui/core/Chip";
import { withStyles } from "@material-ui/core/styles";
import createStyles from "@material-ui/core/styles/createStyles";
import EventIcon from "@material-ui/icons/Event";
import { format, parse, differenceInCalendarDays, startOfDay } from "date-fns";
import { analyseTheme } from "../../analyse/analyseTheme";

const DEADLINE_WARNING_DAYS = 7;
const DEADLINE_DANGER_DAYS = 3;

const styles = () =>
  createStyles({
    deadlineChip: {
      height: 24,
      fontWeight: 500,
      borderRadius: analyseTheme.radiusXs,
    },
    deadlineNormal: {
      color: analyseTheme.textSecondary,
      fontSize: "0.75rem",
    },
  });

const getDeadlineDisplay = (deadline: string | null) => {
  if (!deadline) return null;

  const formatted = format(parse(new Date(deadline)), "DD/MM/YY");
  const daysLeft = differenceInCalendarDays(
    startOfDay(parse(new Date(deadline))),
    startOfDay(new Date()),
  );

  if (daysLeft < 0) {
    return {
      formatted,
      urgency: "danger" as const,
      label: `${formatted} (dépassée)`,
    };
  }
  if (daysLeft <= DEADLINE_DANGER_DAYS) {
    return {
      formatted,
      urgency: "danger" as const,
      label: daysLeft === 0 ? `Aujourd'hui` : `J-${daysLeft}`,
    };
  }
  if (daysLeft <= DEADLINE_WARNING_DAYS) {
    return { formatted, urgency: "warning" as const, label: `J-${daysLeft}` };
  }

  return { formatted, urgency: "normal" as const, label: formatted };
};

interface Props {
  classes: {
    deadlineChip: string;
    deadlineNormal: string;
  };
  deadline: string | null;
}

const DeadlineDisplay = ({ deadline, classes }: Props) => {
  const display = getDeadlineDisplay(deadline);
  if (!display) return null;

  if (display.urgency === "normal") {
    return (
      <span className={classes.deadlineNormal}>Échéance : {display.label}</span>
    );
  }

  const chipColor = display.urgency === "danger" ? "#d32f2f" : "#ed6c02";

  return (
    <Chip
      icon={
        <EventIcon
          style={{ color: chipColor, marginLeft: 4, marginRight: -8, fontSize: 16 }}
        />
      }
      label={`Date limite : ${display.label}`}
      className={classes.deadlineChip}
      style={{
        color: chipColor,
        borderColor: chipColor,
        backgroundColor:
          display.urgency === "danger"
            ? "rgba(211, 47, 47, 0.08)"
            : "rgba(237, 108, 2, 0.08)",
      }}
      variant="outlined"
    />
  );
};

export default withStyles(styles)(DeadlineDisplay);
